import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { API_BASE } from '../utils/api'
import { useCity } from '../context/CityContext'
import MobileSearchForm from './MobileSearchForm'


/* ── Types ── */
type TabKey = 'Residential' | 'Commercial' | 'PG/Co-living' | 'Plot' | 'Land' | 'Bank Auction'

const TABS: { key: TabKey; label: string; icon: string; badge?: string }[] = [
    { key: 'Residential', label: 'Residential', icon: 'fa-solid fa-house-chimney' },
    { key: 'Commercial', label: 'Commercial', icon: 'fa-solid fa-city' },
    { key: 'PG/Co-living', label: 'PG/Co-living', icon: 'fa-solid fa-bed' },
    { key: 'Plot', label: 'Plot', icon: 'fa-solid fa-map-location-dot' },
    { key: 'Land', label: 'Land', icon: 'fa-solid fa-mountain-sun' },
    { key: 'Bank Auction', label: 'Bank Auction Property', icon: 'fa-solid fa-gavel', badge: 'NEW' },
]

/* ── Tab-to-keyword mapping for filtering dynamic types ── */
const TAB_TYPE_KEYWORDS: Record<TabKey, string[]> = {
    'Residential': ['apartment', 'villa', 'house', 'penthouse', 'studio', 'duplex', 'bungalow', 'flat', 'residential', 'bhk'],
    'Commercial': ['office', 'shop', 'showroom', 'warehouse', 'co-working', 'commercial', 'retail', 'space'],
    'PG/Co-living': ['pg', 'co-living', 'hostel', 'paying guest'],
    'Plot': ['plot', 'site'],
    'Land': ['land', 'agricultural', 'industrial', 'farm'],
    'Bank Auction': ['auction', 'bank'],
}

/* ── Fallback static types (used when no DB types match a tab) ── */
const FALLBACK_TYPES: Record<TabKey, string[]> = {
    'Residential': ['Apartment', 'Villa', 'Independent House', 'Penthouse', 'Studio', 'Duplex'],
    'Commercial': ['Office Space', 'Shop / Showroom', 'Warehouse', 'Co-working Space'],
    'PG/Co-living': ['PG for Boys', 'PG for Girls', 'Co-living Space'],
    'Plot': ['Residential Plot', 'Commercial Plot'],
    'Land': ['Agricultural Land', 'Industrial Land'],
    'Bank Auction': ['Residential', 'Commercial', 'Industrial'],
}

/* ── Advanced filter state type ── */
interface AdvFilter {
    subCategory: string
    furnishing: string
    minSize: string
    maxSize: string
    sizeUnit: string
    bathrooms: string
    buildingAge: string
    minPrice: string
    maxPrice: string
    sellType: string
    facing: string
    postedBy: string
    parkingType: string
    constructionStatus: string
    reraRegistered: string
    balcony: string
    postedSince: string
    totalFloors: string
    liftAvailable: boolean
    powerBackup: boolean
    gatedCommunity: boolean
    cornerProperty: boolean
    parkFacing: boolean
    nearbyMetro: boolean
}

const BUDGET_OPTIONS = [
    { label: '30 Lacs', value: '3000000' },
    { label: '40 Lacs', value: '4000000' },
    { label: '50 Lacs', value: '5000000' },
    { label: '60 Lacs', value: '6000000' },
    { label: '75 Lacs', value: '7500000' },
    { label: '90 Lacs', value: '9000000' },
    { label: '1 Cr', value: '10000000' },
    { label: '1.25 Cr', value: '12500000' },
    { label: '1.5 Cr', value: '15000000' },
    { label: '2 Cr', value: '20000000' },
    { label: '3 Cr', value: '30000000' },
    { label: '4 Cr', value: '40000000' },
    { label: '5 Cr', value: '50000000' },
    { label: '10 Cr', value: '100000000' },
    { label: '15 Cr', value: '150000000' },
];


const defaultAdvFilter: AdvFilter = {
    subCategory: '', furnishing: '', minSize: '', maxSize: '', sizeUnit: 'sq.ft',
    bathrooms: '', buildingAge: '', minPrice: '', maxPrice: '', sellType: '',
    facing: '', postedBy: '', parkingType: '', constructionStatus: '',
    reraRegistered: '', balcony: '', postedSince: '', totalFloors: '',
    liftAvailable: false, powerBackup: false, gatedCommunity: false,
    cornerProperty: false, parkFacing: false, nearbyMetro: false,
}

export default function FormTwo() {
    const navigate = useNavigate()
    const [activeTab, setActiveTab] = useState<TabKey>('Residential')
    const [buyMode, setBuyMode] = useState<'Buy' | 'Rent'>('Buy')
    const [showBuyDropdown, setShowBuyDropdown] = useState(false)
    const [selectedType, setSelectedType] = useState('All Residential')
    const { selectedCity: globalCity, setSelectedCity: setGlobalCity, cities } = useCity()
    const [selectedCity, setSelectedCity] = useState('All Cities')
    const [showCityDropdown, setShowCityDropdown] = useState(false)
    const [searchText, setSearchText] = useState('')
    const [suggestions, setSuggestions] = useState<string[]>([])
    const [showSuggestions, setShowSuggestions] = useState(false)
    const [allTypes, setAllTypes] = useState<string[]>([])
    const [allBedrooms, setAllBedrooms] = useState<number[]>([])
    const [showTypeDropdown, setShowTypeDropdown] = useState(false)
    const [showAdvanced, setShowAdvanced] = useState(false)
    const [showBudgetDropdown, setShowBudgetDropdown] = useState(false)
    const [selectedBudget, setSelectedBudget] = useState('Select Budget')
    const [adv, setAdv] = useState<AdvFilter>({ ...defaultAdvFilter })

    const getBudgetText = () => {
        if (!adv.minPrice && !adv.maxPrice) return "Select Budget";
        const minObj = BUDGET_OPTIONS.find(o => o.value === adv.minPrice);
        const maxObj = BUDGET_OPTIONS.find(o => o.value === adv.maxPrice);

        if (minObj && !maxObj) return `Above ${minObj.label}`;
        if (!minObj && maxObj) return `Under ${maxObj.label}`;
        if (minObj && maxObj) return `${minObj.label} - ${maxObj.label}`;
        return "Select Budget";
    };

    /* ── Typing Animation for Placeholder ── */
    const [placeholderText, setPlaceholderText] = useState('')
    const [typingIndex, setTypingIndex] = useState(0)
    const [charIndex, setCharIndex] = useState(0)
    const [featureIndex, setFeatureIndex] = useState(0)
    const features = ['Personal Property Manager', 'Zero Brokerage', 'No Subscription Fees']

    useEffect(() => {
        const timer = setInterval(() => {
            setFeatureIndex(prev => (prev + 1) % features.length)
        }, 3000)
        return () => clearInterval(timer)
    }, [])
    const [isDeleting, setIsDeleting] = useState(false)
    const typingStrings = ['Location', 'Project', 'Developer']

    useEffect(() => {
        const currentString = typingStrings[typingIndex]
        let delay = isDeleting ? 70 : 150 // Speed: typing vs deleting

        // Pauses
        if (!isDeleting && charIndex === currentString.length) {
            delay = 2000 // Pause when finished typing
        } else if (isDeleting && charIndex === 0) {
            delay = 500 // Pause when finished deleting
        }

        const timer = setTimeout(() => {
            if (isDeleting) {
                if (charIndex > 0) {
                    setPlaceholderText(currentString.substring(0, charIndex - 1))
                    setCharIndex(prev => prev - 1)
                } else {
                    setIsDeleting(false)
                    setTypingIndex(prev => (prev + 1) % typingStrings.length)
                }
            } else {
                if (charIndex < currentString.length) {
                    setPlaceholderText(currentString.substring(0, charIndex + 1))
                    setCharIndex(prev => prev + 1)
                } else {
                    setIsDeleting(true)
                }
            }
        }, delay)

        return () => clearTimeout(timer)
    }, [charIndex, isDeleting, typingIndex])

    const inputRef = useRef<HTMLInputElement>(null)
    const wrapperRef = useRef<HTMLDivElement>(null)

    /* ── Fetch dynamic filter options from API ── */
    useEffect(() => {
        fetch(`${API_BASE}/properties/filter-options`)
            .then(r => r.json())
            .then(data => {
                if (data.types) setAllTypes(data.types)
                if (data.bedrooms) setAllBedrooms(data.bedrooms)
            })
            .catch(console.error)
    }, [])

    /* ── Compute dynamic property types for current tab ── */
    const getTypesForTab = (tab: TabKey): string[] => {
        const keywords = TAB_TYPE_KEYWORDS[tab]
        const matched = allTypes.filter(t =>
            keywords.some(kw => t.toLowerCase().includes(kw))
        )
        const types = matched.length > 0 ? matched : FALLBACK_TYPES[tab]
        const allLabel = tab === 'Bank Auction' ? 'All Types' : `All ${tab}`
        return [allLabel, ...types]
    }


    const currentTabTypes = getTypesForTab(activeTab)

    useEffect(() => {
        // Reset type and advanced filters when tab changes
        const types = getTypesForTab(activeTab)
        setSelectedType(types[0])
        setAdv({ ...defaultAdvFilter })
    }, [activeTab, allTypes])

    useEffect(() => {
        if (globalCity && globalCity !== 'Detecting Location...') {
            setSelectedCity(globalCity)
        }
    }, [globalCity])

    useEffect(() => {
        if (searchText.trim().length > 0) {
            const f = cities.filter(c => c.toLowerCase().includes(searchText.toLowerCase()))
            setSuggestions(f.slice(0, 7))
            setShowSuggestions(f.length > 0)
        } else {
            setShowSuggestions(false)
        }
    }, [searchText, cities])

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setShowSuggestions(false)
                setShowBuyDropdown(false)
                setShowCityDropdown(false)
                setShowTypeDropdown(false)
            }
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    const getStatus = () => buyMode === 'Rent' ? 'For Rent' : 'For Sale'

    const handleSearch = (city?: string) => {
        const p = new URLSearchParams()
        p.set('status', getStatus())
        const cityVal = city || (selectedCity !== 'All Cities' ? selectedCity : '') || searchText
        if (cityVal) p.set('city', cityVal)
        if (activeTab === 'Commercial') p.set('type', 'Office')
        if (activeTab === 'Plot') p.set('type', 'Plot')
        if (activeTab === 'Land') p.set('type', 'Land')
        if (selectedType && !selectedType.startsWith('All')) p.set('type', selectedType)

        // ── Advanced filters ──
        if (adv.subCategory) p.set('subCategory', adv.subCategory)
        if (adv.furnishing) p.set('furnishing', adv.furnishing)
        if (adv.minSize) p.set('minArea', adv.minSize)
        if (adv.maxSize) p.set('maxArea', adv.maxSize)
        if (adv.bathrooms) p.set('bathrooms', adv.bathrooms)
        if (adv.buildingAge) p.set('buildingAge', adv.buildingAge)
        if (adv.minPrice) p.set('minPrice', adv.minPrice)
        if (adv.maxPrice) p.set('maxPrice', adv.maxPrice)
        if (adv.sellType) p.set('sellType', adv.sellType)
        if (adv.facing) p.set('facing', adv.facing)
        if (adv.postedBy) p.set('postedByRole', adv.postedBy)
        if (adv.parkingType) p.set('parkingType', adv.parkingType)
        if (adv.constructionStatus) p.set('constructionStatus', adv.constructionStatus)
        if (adv.reraRegistered === 'yes') p.set('reraRegistered', 'true')
        if (adv.balcony) p.set('balcony', adv.balcony)
        if (adv.postedSince) p.set('postedSince', adv.postedSince)
        if (adv.totalFloors) p.set('totalFloors', adv.totalFloors)
        if (adv.liftAvailable) p.set('liftAvailable', 'true')
        if (adv.powerBackup) p.set('powerBackup', 'true')
        if (adv.gatedCommunity) p.set('gatedCommunity', 'true')
        if (adv.cornerProperty) p.set('cornerProperty', 'true')
        if (adv.parkFacing) p.set('parkFacing', 'true')
        if (adv.nearbyMetro) p.set('nearbyMetro', 'true')

        navigate(`/project-list?${p.toString()}`)
        setShowSuggestions(false)
    }


    /* helper: set advanced filter field */
    const setA = (key: keyof AdvFilter, val: any) => setAdv(prev => ({ ...prev, [key]: val }))
    /* helper: toggle chip — select or deselect */
    const toggleChip = (key: keyof AdvFilter, val: string) =>
        setAdv(prev => ({ ...prev, [key]: prev[key] === val ? '' : val }))
    /* count active filters */
    const activeFilterCount = (() => {
        let c = 0
        if (adv.subCategory) c++
        if (adv.furnishing) c++
        if (adv.minSize || adv.maxSize) c++
        if (adv.bathrooms) c++
        if (adv.buildingAge) c++
        if (adv.minPrice || adv.maxPrice) c++
        if (adv.sellType) c++
        if (adv.facing) c++
        if (adv.postedBy) c++
        if (adv.parkingType) c++
        if (adv.constructionStatus) c++
        if (adv.reraRegistered) c++
        if (adv.balcony) c++
        if (adv.liftAvailable) c++
        if (adv.powerBackup) c++
        if (adv.gatedCommunity) c++
        if (adv.cornerProperty) c++
        if (adv.parkFacing) c++
        if (adv.nearbyMetro) c++
        return c
    })()



    return (
        <div className="w-full max-w-[1200px] mx-auto relative decoration-clone mt-6 z-30">
            {/* ──────── DESKTOP TABS ──────── */}
            <div className="hidden lg:flex bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-0 relative z-20 max-w-max mx-auto md:mx-0 p-1.5 gap-1.5">
                {TABS.map((tab, i) => {
                    const active = activeTab === tab.key;
                    return (
                        <React.Fragment key={tab.key}>
                            <button
                                onClick={() => setActiveTab(tab.key)}
                                className={`px-2 lg:px-6 py-2.5 lg:py-3.5 cursor-pointer flex items-center justify-center gap-2 transition-all duration-300 relative
                                    ${active ? 'bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-white shadow-lg' : 'text-gray-600 hover:bg-gray-200 hover:text-[#05144b]'}
                                    font-bold text-[13px] lg:text-[15px] rounded-lg`}
                            >
                                {tab.icon && <i className={`${tab.icon} ${active ? 'text-white' : 'text-gray-400'}`} />}
                                {tab.label}
                                {tab.badge && (
                                    <span className="absolute top-[-2px] right-2 bg-[#ffbb00] text-black text-[8px] font-black px-1.5 py-0.5 rounded uppercase tracking-tighter shadow-sm">
                                        {tab.badge}
                                    </span>
                                )}
                            </button>
                        </React.Fragment>
                    );
                })}


            </div>

            <MobileSearchForm
                tabs={TABS}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                buyMode={buyMode}
                setBuyMode={setBuyMode}
                selectedType={selectedType}
                setSelectedType={setSelectedType}
                selectedCity={selectedCity}
                setSelectedCity={setSelectedCity}
                searchText={searchText}
                setSearchText={setSearchText}
                handleSearch={handleSearch}
                setShowAdvanced={setShowAdvanced}
                cities={cities}
                currentTabTypes={currentTabTypes}
            />

            {/* ──────── MAIN CARD (DESKTOP) ──────── */}
            <div ref={wrapperRef} className="hidden lg:block bg-white rounded-xl lg:rounded-[30px] shadow-[0_20px_60px_rgba(5,20,75,0.08)] border border-gray-100 p-4 lg:p-14 transition-all -mt-4 relative z-10">

                {/* ── Inner Search Bar Container (Dimension Match) ── */}
                <div className="flex flex-col lg:flex-row items-center bg-white rounded-xl lg:rounded-2xl border border-gray-100 shadow-sm p-1.5 lg:p-1.5 w-full lg:h-[60px]">

                    {/* BUY / RENT Dropdown */}
                    <div className="relative w-full lg:w-[110px] h-10 lg:h-full">
                        <button className="w-full h-full flex items-center justify-between px-4 lg:px-5 gap-2 font-black text-[15px] text-gray-900 hover:bg-gray-50 transition-colors"
                            onClick={() => { setShowBuyDropdown(v => !v); setShowCityDropdown(false); setShowTypeDropdown(false) }}>
                            {buyMode}
                            <i className="fa-solid fa-chevron-down text-[11px] text-gray-400" />
                        </button>
                        {showBuyDropdown && (
                            <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 z-[9999] min-w-[140px] animate-in fade-in slide-in-from-top-2 duration-300">
                                {(['Buy', 'Rent'] as const).map(m => (
                                    <button key={m} className={`w-full block text-left px-5 py-3 text-sm transition-all border-b border-gray-50 last:border-0 ${buyMode === m ? 'bg-blue-50 text-[#05144b] font-black' : 'text-gray-600 font-bold hover:bg-gray-50'}`}
                                        onMouseDown={() => { setBuyMode(m); setShowBuyDropdown(false) }}>
                                        {m}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="hidden lg:block h-8 w-[1px] bg-gray-200 mx-1" />

                    {/* CITY Dropdown */}
                    <div className="relative w-full lg:w-auto lg:min-w-[170px] h-10 lg:h-full">
                        <button className="w-full h-full flex items-center justify-between px-4 lg:px-5 gap-2 font-black text-[15px] text-gray-900 hover:bg-gray-50 transition-colors"
                            onClick={() => { setShowCityDropdown(v => !v); setShowBuyDropdown(false); setShowTypeDropdown(false) }}>
                            <span className="whitespace-nowrap">{selectedCity}</span>
                            <i className="fa-solid fa-chevron-down text-[11px] text-gray-400" />
                        </button>
                        {showCityDropdown && (
                            <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-y-auto z-[9999] max-h-72 min-w-[200px] animate-in fade-in slide-in-from-top-2 duration-300">
                                {['All Cities', ...cities].map(c => (
                                    <button key={c} className={`w-full block text-left px-5 py-3 text-sm transition-all border-b border-gray-50 last:border-0 ${selectedCity === c ? 'bg-blue-50 text-[#05144b] font-black' : 'text-gray-600 font-bold hover:bg-gray-50'}`}
                                        onMouseDown={() => {
                                            setSelectedCity(c);
                                            setGlobalCity(c);
                                            setShowCityDropdown(false)
                                        }}>
                                        {c}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="hidden lg:block h-8 w-[1px] bg-gray-200 mx-1" />

                    {/* TEXT INPUT (Locality/Project) */}
                    <div className="relative flex-1 h-10 lg:h-full">
                        <div className="flex items-center h-full px-4 lg:px-6">
                            <input
                                ref={inputRef}
                                type="text"
                                value={searchText}
                                onChange={e => setSearchText(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') handleSearch() }}
                                onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true) }}
                                placeholder={placeholderText || "Location Project Developer"}
                                className="w-full h-full border-none focus:ring-0 focus:outline-none bg-transparent text-[16px] font-medium text-gray-400 placeholder-gray-300"
                            />
                            <i className="fa-solid fa-magnifying-glass text-gray-400 text-xl ml-2 opacity-50" />
                        </div>

                        {showSuggestions && (suggestions.length > 0) && (
                            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-[9999]">
                                {suggestions.map(city => (
                                    <button
                                        key={city}
                                        onMouseDown={() => {
                                            setSearchText(city)
                                            setShowSuggestions(false)
                                            handleSearch(city)
                                        }}
                                        className="w-full flex items-center gap-3 px-5 py-3 text-sm text-gray-700 font-bold hover:bg-blue-50 hover:text-[#05144b] transition-all border-b border-gray-50 last:border-0"
                                    >
                                        <i className="fa-regular fa-compass text-blue-900/40 text-xs" />
                                        {city}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="hidden lg:block h-8 w-[1px] bg-gray-200 mx-1" />

                    {/* PROPERTY TYPE Dropdown */}
                    <div className="relative w-full lg:w-[180px] h-10 lg:h-full">
                        <button className="w-full h-full flex items-center justify-between px-4 lg:px-6 gap-2 font-bold text-[15px] text-gray-900 hover:bg-gray-50 transition-colors"
                            onClick={() => { setShowTypeDropdown(v => !v); setShowBuyDropdown(false); setShowCityDropdown(false) }}>
                            <span className="truncate">{selectedType}</span>
                            <i className="fa-solid fa-chevron-down text-[11px] text-gray-400" />
                        </button>
                        {showTypeDropdown && (
                            <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-y-auto z-[9999] max-h-72 min-w-[220px]">
                                {currentTabTypes.map((t: string) => (
                                    <button key={t} className={`w-full block text-left px-5 py-3 text-sm transition-all border-b border-gray-50 last:border-0 ${selectedType === t ? 'bg-blue-50 text-[#05144b] font-black' : 'text-gray-600 font-bold hover:bg-gray-50'}`}
                                        onMouseDown={() => { setSelectedType(t); setShowTypeDropdown(false) }}>
                                        {t}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <button
                        onClick={() => handleSearch()}
                        className="w-full lg:w-[140px] h-11 lg:h-full bg-gradient-to-r from-[#05144b] to-[#4c1d95] hover:opacity-90 text-white font-black text-[16px] tracking-[1px] transition-all uppercase rounded-lg shadow-lg shadow-blue-900/10 flex items-center justify-center gap-2"
                    >
                        SEARCH
                    </button>
                </div>

                {/* ── Advanced Search & Trust Badges ── */}
                <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-4">
                    <button
                        onClick={() => setShowAdvanced(true)}
                        className="flex items-center gap-2 px-6 py-2 bg-white hover:bg-gray-50 text-[#05144b] font-bold text-[13px] uppercase tracking-widest rounded-xl border border-gray-200 transition-all shadow-sm hover:shadow-md active:scale-95 shrink-0"
                    >
                        <i className="fa-solid fa-sliders text-[#c88128]" />
                        Advanced Search
                    </button>

                    {/* Select Budget Dropdown */}
                    <div className="relative shrink-0">
                        <button
                            onClick={() => setShowBudgetDropdown(!showBudgetDropdown)}
                            className="flex items-center justify-between gap-3 px-6 py-2 bg-white text-[#05144b] font-bold text-[14px] rounded-xl border-[1.5px] border-blue-400/50 hover:border-blue-400 transition-all shadow-sm min-w-[160px]"
                        >
                            <span className="truncate">{getBudgetText()}</span>
                            <i className={`fa-solid fa-chevron-down text-[10px] transition-transform ${showBudgetDropdown ? 'rotate-180' : ''}`} />
                        </button>

                        {showBudgetDropdown && (
                            <div className="absolute top-full left-0 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-[9999] w-[340px] animate-in fade-in slide-in-from-top-2 duration-300 p-4">
                                <h3 className="text-[14px] font-bold text-gray-900 mb-3">Budget Range</h3>

                                <div className="flex items-center gap-3 mb-4">
                                    <div className="flex-1 relative">
                                        <select
                                            value={adv.minPrice}
                                            onChange={(e) => setAdv(prev => ({ ...prev, minPrice: e.target.value }))}
                                            className="w-full h-10 pl-3 pr-8 bg-white border border-gray-200 rounded-xl text-[13px] font-bold text-gray-900 appearance-none focus:ring-2 focus:ring-primary/10 focus:border-primary cursor-pointer"
                                        >
                                            <option value="">Min Price</option>
                                            {BUDGET_OPTIONS.map(opt => (
                                                <option key={`min-${opt.value}`} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                        <i className="fa-solid fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-[10px]" />
                                    </div>

                                    <div className="text-gray-300 font-light">—</div>

                                    <div className="flex-1 relative">
                                        <select
                                            value={adv.maxPrice}
                                            onChange={(e) => setAdv(prev => ({ ...prev, maxPrice: e.target.value }))}
                                            className="w-full h-10 pl-3 pr-8 bg-white border border-gray-200 rounded-xl text-[13px] font-bold text-gray-900 appearance-none focus:ring-2 focus:ring-primary/10 focus:border-primary cursor-pointer"
                                        >
                                            <option value="">Max Price</option>
                                            {BUDGET_OPTIONS.map(opt => (
                                                <option key={`max-${opt.value}`} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                        <i className="fa-solid fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-[10px]" />
                                    </div>
                                </div>

                                <div className="h-[1px] bg-gray-100 -mx-4 mb-2" />

                                <div className="flex items-center justify-between">
                                    <button
                                        onClick={() => {
                                            setAdv(prev => ({ ...prev, minPrice: '', maxPrice: '' }));
                                            setShowBudgetDropdown(false);
                                        }}
                                        className="text-[13px] font-bold text-gray-600 hover:text-[#05144b] transition-colors"
                                    >
                                        Clear
                                    </button>
                                    <button
                                        onClick={() => setShowBudgetDropdown(false)}
                                        className="px-6 py-1.5 bg-gradient-to-r from-[#05144b] to-[#4c1d95] hover:opacity-90 text-white font-bold text-[13px] rounded-lg transition-all active:scale-95"
                                    >
                                        Done
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-3 bg-white px-3 py-2 rounded-xl border border-gray-100 overflow-hidden min-w-[300px]">
                        <div className="flex items-center gap-2 shrink-0">
                            <i className="fa-solid fa-circle-check text-[#4c1d95] text-sm" />
                            <span className="text-[11px] font-bold text-black uppercase tracking-wider">Features:</span>
                        </div>
                        <div className="relative flex-1 h-5 overflow-hidden">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={features[featureIndex]}
                                    initial={{ y: 20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    exit={{ y: -20, opacity: 0 }}
                                    transition={{ duration: 0.5, ease: "easeOut" }}
                                    className="absolute inset-0 flex items-center"
                                >
                                    <span className="text-[13px] font-semibold text-gray-700 whitespace-nowrap">
                                        {features[featureIndex]}
                                    </span>
                                </motion.div>
                            </AnimatePresence>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-xl border border-gray-100 shrink-0">
                        <i className="fa-solid fa-circle-check text-[#4c1d95] text-sm" />
                        <span className="text-[13px] font-bold text-black whitespace-nowrap">RERA Approved</span>
                    </div>
                </div>
            </div>

            {/* ══════════════════════════════════════════════════════
                    ADVANCED FILTER PANEL — Fixed Overlay Modal (using Portal)
                ══════════════════════════════════════════════════════ */}
            {showAdvanced && createPortal(
                <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[99999999] flex justify-center items-center p-3 md:p-8"
                    onClick={(e) => { if (e.target === e.currentTarget) setShowAdvanced(false) }}>

                    <div className="w-full max-w-5xl bg-white rounded-[2rem] shadow-2xl flex flex-col max-h-[92vh] animate-in slide-in-from-bottom-10 fade-in duration-300 relative border border-white/20">

                        {/* Modern Header with Gradient */}
                        <div className="relative overflow-hidden rounded-t-[2rem] shrink-0">
                            <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-primary/5" />
                            <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-100 relative">
                                <div className="flex items-center gap-3 md:gap-4">
                                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-2xl bg-gradient-to-br from-primary to-primary/80 shadow-lg shadow-primary/30 flex items-center justify-center shrink-0">
                                        <svg className="w-5 h-5 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h2 className="text-lg md:text-2xl font-bold text-gray-900 leading-tight">
                                            Advanced Search
                                            {activeFilterCount > 0 && (
                                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] md:text-xs font-medium bg-primary/10 text-primary">
                                                    {activeFilterCount}
                                                </span>
                                            )}
                                        </h2>
                                        <p className="hidden md:block text-sm text-gray-500 mt-1">Refine your property search with precision</p>
                                    </div>
                                </div>
                                <button onClick={() => setShowAdvanced(false)}
                                    className="w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-900 flex items-center justify-center transition-all hover:rotate-90 active:scale-90 shadow-sm border border-slate-200">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Scrollable Content */}
                        <div className="flex-1 overflow-y-auto px-6 py-8 space-y-10 custom-scrollbar" style={{ fontFamily: "'Poppins', sans-serif" }}>



                            {/* Posted Since Section */}
                            <section>
                                <div className="flex items-center gap-2 mb-6">
                                    <div className="w-1.5 h-7 bg-primary rounded-full" />
                                    <h3 className="text-[16px] font-bold text-gray-900 uppercase tracking-wide">Posted Since</h3>
                                </div>
                                <div className="relative max-w-sm">
                                    <select 
                                        value={adv.postedSince}
                                        onChange={(e) => setA('postedSince', e.target.value)}
                                        className="w-full h-12 pl-4 pr-10 bg-gray-50 border border-gray-200 rounded-xl text-sm font-bold text-gray-900 appearance-none focus:ring-4 focus:ring-primary/10 focus:border-primary outline-none transition-all cursor-pointer"
                                    >
                                        <option value="">Select Posted Since</option>
                                        <option value="last_24_hours">Last 24 Hours</option>
                                        <option value="last_3_days">Last 3 Days</option>
                                        <option value="last_week">Last Week</option>
                                        <option value="last_month">Last Month</option>
                                    </select>
                                    <i className="fa-solid fa-chevron-down absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                </div>
                            </section>

                            {/* Property Type Section */}
                            <section>
                                <div className="flex items-center gap-2 mb-6">
                                    <div className="w-1.5 h-7 bg-primary rounded-full" />
                                    <h3 className="text-[16px] font-bold text-gray-900 uppercase tracking-wide">Property Type</h3>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {(activeTab === 'Residential'
                                        ? ['Flat', 'Villa', 'Penthouse', 'Duplex', 'Bungalow', 'Studio']
                                        : activeTab === 'Commercial'
                                            ? ['Office Space', 'Shop', 'Showroom', 'Warehouse', 'Co-working']
                                            : activeTab === 'PG/Co-living'
                                                ? ['PG for Boys', 'PG for Girls', 'Co-living Space']
                                                : activeTab === 'Plot'
                                                    ? ['Residential Plot', 'Commercial Plot', 'Industrial Plot']
                                                    : activeTab === 'Land'
                                                        ? ['Agricultural Land', 'Industrial Land', 'Farm Land', 'NA Land']
                                                        : ['Residential', 'Commercial', 'Industrial', 'Land']
                                    ).map(v => (
                                        <button key={v}
                                            onClick={() => toggleChip('subCategory', v)}
                                            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 
                                                ${adv.subCategory === v
                                                    ? 'bg-primary text-white shadow-md shadow-primary/30 scale-105'
                                                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-900 border border-gray-200'}`}>
                                            {v}
                                        </button>
                                    ))}
                                </div>
                            </section>

                            {/* Area Section */}
                            <section>
                                <div className="flex items-center gap-2 mb-6">
                                    <div className="w-1.5 h-7 bg-primary rounded-full" />
                                    <h3 className="text-[16px] font-bold text-gray-900 uppercase tracking-wide">Area in sq. ft</h3>
                                </div>
                                <div className="flex items-center gap-4 max-w-lg">
                                    <div className="flex-1">
                                        <input 
                                            type="number" 
                                            placeholder="Min"
                                            value={adv.minSize}
                                            onChange={(e) => setA('minSize', e.target.value)}
                                            className="w-full h-12 px-4 bg-gray-50 border border-gray-200 rounded-xl text-sm font-bold text-gray-900 focus:ring-4 focus:ring-primary/10 focus:border-primary outline-none transition-all"
                                        />
                                    </div>
                                    <div className="text-gray-400 font-bold uppercase text-[10px]">to</div>
                                    <div className="flex-1">
                                        <input 
                                            type="number" 
                                            placeholder="50000+"
                                            value={adv.maxSize}
                                            onChange={(e) => setA('maxSize', e.target.value)}
                                            className="w-full h-12 px-4 bg-gray-50 border border-gray-200 rounded-xl text-sm font-bold text-gray-900 focus:ring-4 focus:ring-primary/10 focus:border-primary outline-none transition-all"
                                        />
                                    </div>
                                </div>
                            </section>

                            {/* Total Floors & Bathrooms */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <section>
                                    <div className="flex items-center gap-2 mb-6">
                                        <div className="w-1.5 h-7 bg-primary rounded-full" />
                                        <h3 className="text-[16px] font-bold text-gray-900 uppercase tracking-wide">Total Floors</h3>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {['1', '2', '3', '4', '5', '6', '7', '+ More'].map(v => (
                                            <button key={v}
                                                onClick={() => toggleChip('totalFloors', v)}
                                                className={`w-12 h-12 rounded-xl text-sm font-black transition-all duration-200 flex items-center justify-center
                                                    ${adv.totalFloors === v
                                                        ? 'bg-primary text-white shadow-md shadow-primary/30 scale-105'
                                                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'}`}>
                                                {v}
                                            </button>
                                        ))}
                                    </div>
                                </section>

                                <section>
                                    <div className="flex items-center gap-2 mb-6">
                                        <div className="w-1.5 h-7 bg-primary rounded-full" />
                                        <h3 className="text-[16px] font-bold text-gray-900 uppercase tracking-wide">Bathrooms</h3>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {['1', '2', '3', '4', '5', '6', '7'].map(v => (
                                            <button key={v}
                                                onClick={() => toggleChip('bathrooms', v)}
                                                className={`w-12 h-12 rounded-xl text-sm font-black transition-all duration-200 flex items-center justify-center
                                                    ${adv.bathrooms === v
                                                        ? 'bg-primary text-white shadow-md shadow-primary/30 scale-105'
                                                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'}`}>
                                                {v}
                                            </button>
                                        ))}
                                    </div>
                                </section>
                            </div>

                            {/* Age of Construction & Furnishing */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <section>
                                    <div className="flex items-center gap-2 mb-6">
                                        <div className="w-1.5 h-7 bg-primary rounded-full" />
                                        <h3 className="text-[16px] font-bold text-gray-900 uppercase tracking-wide">Age of Construction</h3>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {['New Launch', 'Under Construction', 'Ready to Move', '1-5 Years', '5-10 Years', '10+ Years'].map(v => (
                                            <button key={v}
                                                onClick={() => toggleChip('buildingAge', v)}
                                                className={`px-4 py-2 rounded-xl text-sm font-bold transition-all duration-200
                                                    ${adv.buildingAge === v
                                                        ? 'bg-primary text-white shadow-md shadow-primary/30 scale-105'
                                                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'}`}>
                                                {v}
                                            </button>
                                        ))}
                                    </div>
                                </section>

                                <section>
                                    <div className="flex items-center gap-2 mb-6">
                                        <div className="w-1.5 h-7 bg-primary rounded-full" />
                                        <h3 className="text-[16px] font-bold text-gray-900 uppercase tracking-wide">Furnishing Status</h3>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {['Unfurnished', 'Semi-Furnished', 'Fully Furnished'].map(v => (
                                            <button key={v}
                                                onClick={() => toggleChip('furnishing', v)}
                                                className={`px-4 py-2 rounded-xl text-sm font-bold transition-all duration-200
                                                    ${adv.furnishing === v
                                                        ? 'bg-primary text-white shadow-md shadow-primary/30 scale-105'
                                                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'}`}>
                                                {v}
                                            </button>
                                        ))}
                                    </div>
                                </section>
                            </div>

                            {/* Parking & Construction Status */}
                            {(activeTab === 'Residential' || activeTab === 'Commercial' || activeTab === 'Bank Auction') && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <section>
                                        <div className="flex items-center gap-2 mb-4">
                                            <div className="w-1 h-6 bg-primary rounded-full" />
                                            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Parking Type</h3>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {['Open Parking', 'Covered Parking'].map(v => (
                                                <button key={v}
                                                    onClick={() => toggleChip('parkingType', v)}
                                                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 
                      ${adv.parkingType === v
                                                            ? 'bg-primary text-white shadow-md shadow-primary/30'
                                                            : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'}`}>
                                                    {v}
                                                </button>
                                            ))}
                                        </div>
                                    </section>

                                    <section>
                                        <div className="flex items-center gap-2 mb-4">
                                            <div className="w-1 h-6 bg-primary rounded-full" />
                                            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Construction Status</h3>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {['Ready to Move', 'Under Construction', 'New Launch', 'Possession Soon'].map(v => (
                                                <button key={v}
                                                    onClick={() => toggleChip('constructionStatus', v)}
                                                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 
                      ${adv.constructionStatus === v
                                                            ? 'bg-primary text-white shadow-md shadow-primary/30'
                                                            : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'}`}>
                                                    {v}
                                                </button>
                                            ))}
                                        </div>
                                    </section>
                                </div>
                            )}

                            {/* RERA & Balcony */}
                            {(activeTab === 'Residential' || activeTab === 'Commercial' || activeTab === 'Plot') && (
                                <div className={`grid grid-cols-1 ${activeTab === 'Residential' ? 'md:grid-cols-2' : ''} gap-8`}>
                                    <section>
                                        <div className="flex items-center gap-2 mb-6">
                                            <div className="w-1.5 h-7 bg-primary rounded-full" />
                                            <h3 className="text-[16px] font-bold text-gray-900 uppercase tracking-wide">RERA Certified</h3>
                                        </div>
                                        <div className="flex gap-3">
                                            {[
                                                { label: 'Yes', val: 'yes', icon: '✓' },
                                                { label: 'No', val: 'no', icon: '✗' }
                                            ].map(({ label, val, icon }) => (
                                                <button key={val}
                                                    onClick={() => toggleChip('reraRegistered', val)}
                                                    className={`flex-1 max-w-[120px] px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2
                       ${adv.reraRegistered === val
                                                            ? 'bg-primary text-white shadow-md shadow-primary/30'
                                                            : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'}`}>
                                                    <span>{icon}</span>
                                                    {label}
                                                </button>
                                            ))}
                                        </div>
                                    </section>

                                    {activeTab === 'Residential' && (
                                        <section>
                                            <div className="flex items-center gap-2 mb-6">
                                                <div className="w-1.5 h-7 bg-primary rounded-full" />
                                                <h3 className="text-[16px] font-bold text-gray-900 uppercase tracking-wide">Balconies</h3>
                                            </div>
                                            <div className="flex gap-2">
                                                {['1', '2', '3', '4+'].map(v => (
                                                    <button key={v}
                                                        onClick={() => toggleChip('balcony', v === '4+' ? '4' : v)}
                                                        className={`w-12 h-12 rounded-xl text-sm font-medium transition-all duration-200 flex items-center justify-center
                        ${adv.balcony === (v === '4+' ? '4' : v)
                                                                ? 'bg-primary text-white shadow-md shadow-primary/30'
                                                                : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'}`}>
                                                        {v}
                                                    </button>
                                                ))}
                                            </div>
                                        </section>
                                    )}
                                </div>
                            )}

                            {/* Premium Features Grid */}
                            <section>
                                <div className="flex items-center gap-2 mb-6">
                                    <div className="w-1.5 h-7 bg-primary rounded-full" />
                                    <h3 className="text-[16px] font-bold text-gray-900 uppercase tracking-wide">Premium Features</h3>
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                                    {(activeTab === 'Residential'
                                        ? [
                                            { key: 'liftAvailable', label: 'Lift System', icon: 'M5 13l4 4L19 7' },
                                            { key: 'powerBackup', label: 'Power Backup', icon: 'M13 10V3L4 14h7v7l9-11h-7z' },
                                            { key: 'gatedCommunity', label: 'Gated Security', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
                                            { key: 'cornerProperty', label: 'Corner Unit', icon: 'M4 4v16h16' },
                                            { key: 'parkFacing', label: 'Park Facing', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
                                            { key: 'nearbyMetro', label: 'Metro Access', icon: 'M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7' }
                                        ]
                                        : activeTab === 'Commercial'
                                            ? [
                                                { key: 'liftAvailable', label: 'Lift System', icon: 'M5 13l4 4L19 7' },
                                                { key: 'powerBackup', label: 'Power Backup', icon: 'M13 10V3L4 14h7v7l9-11h-7z' },
                                                { key: 'cornerProperty', label: 'Corner Unit', icon: 'M4 4v16h16' },
                                                { key: 'nearbyMetro', label: 'Highway Access', icon: 'M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7' }
                                            ]
                                            : activeTab === 'Plot'
                                                ? [
                                                    { key: 'cornerProperty', label: 'Corner Plot', icon: 'M4 4v16h16' },
                                                    { key: 'parkFacing', label: 'Park Facing', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
                                                    { key: 'nearbyMetro', label: 'Road Access', icon: 'M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7' },
                                                    { key: 'gatedCommunity', label: 'Gated Enclave', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' }
                                                ]
                                                : [
                                                    { key: 'liftAvailable', label: 'Lift Access', icon: 'M5 13l4 4L19 7' },
                                                    { key: 'powerBackup', label: 'Power Backup', icon: 'M13 10V3L4 14h7v7l9-11h-7z' },
                                                    { key: 'nearbyMetro', label: 'Prime Location', icon: 'M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7' }
                                                ]
                                    ).map(({ key, label, icon }) => (
                                        <button key={key}
                                            onClick={() => setA(key as keyof AdvFilter, !(adv[key as keyof AdvFilter] as boolean))}
                                            className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 group
                  ${adv[key as keyof AdvFilter]
                                                    ? 'bg-primary text-white shadow-md shadow-primary/30'
                                                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'}`}>
                                            <svg className={`w-4 h-4 ${adv[key as keyof AdvFilter] ? 'text-white' : 'text-gray-400 group-hover:text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
                                            </svg>
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </section>
                        </div>

                        {/* Footer Actions */}
                        <div className="border-t border-gray-100 bg-gray-50/50 px-6 py-4 rounded-b-3xl">
                            <div className="flex items-center justify-end gap-3">
                                <button
                                    onClick={() => setAdv({ ...defaultAdvFilter })}
                                    className="px-5 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-all active:scale-95 flex items-center gap-2">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    Reset
                                </button>
                                <button
                                    onClick={() => { handleSearch(); setShowAdvanced(false) }}
                                    className="px-6 py-2.5 rounded-xl bg-primary text-white font-medium text-sm shadow-lg shadow-primary/30 hover:shadow-primary/40 hover:-translate-y-0.5 transition-all active:scale-95 flex items-center gap-2">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                    Apply Filters
                                </button>
                            </div>
                        </div>

                    </div>
                </div>,
                document.body
            )}
        </div>
    )
}